import { createReducer } from '@common/redux-utils';
import {
  ServiceWorkOrderViewModel,
  ServiceWorkOrderSearchViewModel,
  ContractCviViewModel,
  ContractExclusionViewModel,
  ContractTotalCostHistoryViewModel,
  TripStatusLogViewModel
} from '@models/ServiceWorkOrder';
import {
  ServiceWorkOrderActionType,
  SaveServiceWorkOrderStartAction,
  SaveServiceWorkOrderSuccessAction,
  SaveServiceWorkOrderFailureAction,
  GetServiceWorkOrderContractStartAction,
  GetServiceWorkOrderContractSuccessAction,
  GetServiceWorkOrderContractFailureAction,
  SelectCVIAction,
  GetServiceWorkOrderContractCVIStartAction,
  GetServiceWorkOrderContractCVISuccessAction,
  GetServiceWorkOrderContractCVIFailureAction,
  AddNewInventoryAction,
  AddNewExclusionSuccessAction,
  SaveServiceWorkOrderCreateAction,
  GetSWOStartAction,
  GetSWOSuccessAction,
  GetSWOFailureAction,
  AssignVendorStartAction,
  AssignVendorSuccessAction,
  AssignVendorFailureAction,
  ResetSaveAndCloseAction,
  RedispatchSWOFailureAction,
  RedispatchSWOStartAction,
  RedispatchSWOSuccessAction,
  GetSwoAppointmentsStartAction,
  GetSwoAppointmentsSuccessAction,
  GetSwoAppointmentsFailureAction,
  GetSwoInvoicesStartAction,
  GetSwoInvoicesSuccessAction,
  GetSwoInvoicesFailureAction,
  AddSelectedCVIAction,
  SetEditPMAction,
  LoadPropertyManagerAction,
  ClearPropertyManagerAction,
  StoreVendorAction,
  SetTypeIdAction,
  SetTypesAction,
  SetSecondOpinionSWOAction
} from './service-work-order.actions';
import { OptionsViewModel } from '@models/common.models';
import {
  GetInventoryListStartAction,
  GetInventoryListSuccessAction,
  GetInventoryListFailureAction,
  AddNewExclusionStartAction,
  AddNewExclusionFailureAction,
  GetExclusionListStartAction,
  GetExclusionListSuccessAction,
  GetExclusionListFailureAction,
  GetExclusionTypesStartAction,
  GetExclusionTypesSuccessAction,
  GetExclusionTypesFailureAction,
  SaveNotesAction,
  GetContractCostHistoryStartAction,
  GetContractCostHistorySuccessAction,
  GetContractCostHistoryFailureAction,
  EditExclusionStartAction,
  EditExclusionSuccessAction,
  EditExclusionFailureAction,
  LoadSWOAction
} from './service-work-order.actions';
import {
  SearchServiceWorkOrderStartAction,
  SearchServiceWorkOrderSuccessAction,
  SearchServiceWorkOrderFailureAction,
  ResetSWOSearchAction,
  SaveServiceFeePaymentInfoStartAction,
  SaveServiceFeePaymentInfoSuccessAction,
  SaveServiceFeePaymentInfoFailureAction
} from './service-work-order.actions';
import { CustomerViewModel } from '@models/Customer';
import { SwoAppointmentViewModel } from '@models/SwoAppointment';
import { InvoiceViewModel } from '@models/Invoice';
import {
  GetTripStatusLogStartAction,
  GetTripStatusLogFailureAction,
  GetTripStatusLogSuccessAction,
  ClearSwoDetailsAction
} from './service-work-order.actions';
import { PropertyManagerViewModel } from '@models/PropertyManager';
import { SetSwoTicketIdAction } from './service-work-order.actions';

export interface ServiceWorkOrderState {
  readonly message: string;
  readonly id: number;
  readonly serviceWorkOrderVM?: ServiceWorkOrderViewModel;
  readonly customerVM?: CustomerViewModel;
  readonly query: string | null;
  readonly showInactive?: boolean | undefined;
  readonly loading: boolean;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly pageCount: number;
  readonly SWOList: Array<ServiceWorkOrderSearchViewModel>;
  readonly statuses: Array<OptionsViewModel>;
  readonly types: Array<OptionsViewModel>;
  readonly cvis: Array<OptionsViewModel>;
  readonly brands: Array<OptionsViewModel>;
  readonly callReasons: Array<OptionsViewModel>;
  readonly showExpired?: boolean;
  readonly orderBy: string;
  readonly totalPrice: number;
  readonly inventoryList: Array<ContractCviViewModel>;
  readonly exclusionList: Array<ContractExclusionViewModel>;
  readonly selectedCVIs: Array<number>;
  readonly exclusionTypes?: Array<OptionsViewModel>;
  readonly firstBilling?: OptionsViewModel;
  readonly laterBills?: OptionsViewModel;
  readonly costHistory: ContractTotalCostHistoryViewModel[];
  readonly costHistoryLoading: boolean;
  readonly exclusionsLoading: boolean;
  readonly inventoryLoading: boolean;
  readonly showSaveAndClose: boolean;
  readonly swoAppointments: Array<SwoAppointmentViewModel>;
  readonly swoInvoices: Array<InvoiceViewModel>;
  readonly tripStatusLog: Array<TripStatusLogViewModel>;
  readonly editPM?: boolean;
  readonly propMngrId?: PropertyManagerViewModel;
  readonly linkTicketId?: number;
}

export const initialServiceWorkOrderState: ServiceWorkOrderState = {
  message: '',
  id: 0,
  serviceWorkOrderVM: { id: 0 } as ServiceWorkOrderViewModel, // , serviceFee: 75 } as ServiceWorkOrderViewModel,
  query: '',
  showInactive: false,
  loading: false,
  pageNumber: 0,
  pageSize: 10,
  pageCount: 10,
  orderBy: '',
  SWOList: [] as Array<ServiceWorkOrderSearchViewModel>,
  statuses: [] as Array<OptionsViewModel>,
  types: [] as Array<OptionsViewModel>,
  cvis: [] as Array<OptionsViewModel>,
  brands: [] as Array<OptionsViewModel>,
  callReasons: [] as Array<OptionsViewModel>,
  totalPrice: 75,
  inventoryList: [] as Array<ContractCviViewModel>,
  exclusionList: [] as Array<ContractExclusionViewModel>,
  selectedCVIs: [] as Array<number>,
  costHistory: [] as Array<ContractTotalCostHistoryViewModel>,
  exclusionsLoading: false,
  costHistoryLoading: false,
  inventoryLoading: false,
  showSaveAndClose: false,
  swoAppointments: [],
  swoInvoices: [],
  tripStatusLog: [] as Array<TripStatusLogViewModel>,
  editPM: false
};

/********* GetSwo ******/
function handleGetSwoStart(state: ServiceWorkOrderState, action: GetSWOStartAction) {
  return {
    ...state,
    loading: true,
    message: action.type
  };
}

function handleGetSwoSuccess(state: ServiceWorkOrderState, action: GetSWOSuccessAction) {
  return {
    ...state,
    loading: false,
    serviceWorkOrderVM: action.swoVM
  };
}

function handleGetSwoFailure(state: ServiceWorkOrderState, action: GetSWOFailureAction) {
  return {
    ...state,
    loading: false,
    message: action.errorMessage
  };
}

/********** SaveServiceWorkOrder **********/
function handleSaveServiceWorkOrderStart(
  state: ServiceWorkOrderState,
  action: SaveServiceWorkOrderStartAction
): ServiceWorkOrderState {
  return {
    ...state
  };
}
function handleSaveServiceWorkOrderSuccess(
  state: ServiceWorkOrderState,
  action: SaveServiceWorkOrderSuccessAction
): ServiceWorkOrderState {
  if (!action.id) {
    return state;
  }
  return {
    ...state,
    id: action.id,
    serviceWorkOrderVM: action.swoVM
  };
}
function handleSaveServiceWorkOrderFailure(
  state: ServiceWorkOrderState,
  action: SaveServiceWorkOrderFailureAction
): ServiceWorkOrderState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    message: action.errorMessage
  };
}

/********** Search SWO ***********/
function handleResetSWOSearch(state: ServiceWorkOrderState, action: ResetSWOSearchAction): ServiceWorkOrderState {
  return {
    ...state,
    pageNumber: 0,
    pageSize: 10,
    pageCount: 10,
    orderBy: '',
    SWOList: [] as Array<ServiceWorkOrderSearchViewModel>
  };
}

function handleSearchServiceWorkOrderStart(
  state: ServiceWorkOrderState,
  action: SearchServiceWorkOrderStartAction
): ServiceWorkOrderState {
  return {
    ...state,
    query: action.query,
    showInactive: action.showInactive,
    loading: true
  };
}
function handleSearchServiceWorkOrderSuccess(
  state: ServiceWorkOrderState,
  action: SearchServiceWorkOrderSuccessAction
): ServiceWorkOrderState {
  return {
    ...state,
    pageNumber: action.result.page,
    pageCount: action.result.totalResults <= 0 ? 0 : Math.ceil(action.result.totalResults / action.result.pageSize),
    pageSize: action.pageSize,
    SWOList: action.result.results,
    orderBy: action.orderBy,
    loading: false
  };
}
function handleSearchServiceWorkOrderFailure(
  state: ServiceWorkOrderState,
  action: SearchServiceWorkOrderFailureAction
): ServiceWorkOrderState {
  return {
    ...state,
    message: action.error,
    loading: false
  };
}

/********** GetServiceWorkOrderContract **********/
function handleGetServiceWorkOrderContractStart(
  state: ServiceWorkOrderState,
  action: GetServiceWorkOrderContractStartAction
): ServiceWorkOrderState {
  return {
    ...state
  };
}
function handleGetServiceWorkOrderContractSuccess(
  state: ServiceWorkOrderState,
  action: GetServiceWorkOrderContractSuccessAction
): ServiceWorkOrderState {
  if (!action.swoContract && !action.customer) {
    return state;
  }
  return {
    ...state,
    serviceWorkOrderVM: { ...action.swoContract }, // , serviceFee: 75 }, // service fee hardcoded at $75 until we get more info on if it's ever supposed to be different
    customerVM: action.customer
  };
}
function handleGetServiceWorkOrderContractFailure(
  state: ServiceWorkOrderState,
  action: GetServiceWorkOrderContractFailureAction
): ServiceWorkOrderState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    message: action.errorMessage
  };
}

function handleLoadPropertyManager(
  state: ServiceWorkOrderState,
  action: LoadPropertyManagerAction
): ServiceWorkOrderState {
  if (!action.propertyManager) {
    return state;
  }
  return {
    ...state,
    propMngrId: action.propertyManager,
    serviceWorkOrderVM: {
      ...state.serviceWorkOrderVM!,
      propertyManager: action.propertyManager
    }
  };
}

function handleClearPropertyManager(
  state: ServiceWorkOrderState,
  action: ClearPropertyManagerAction
): ServiceWorkOrderState {
  return {
    ...state,
    propMngrId: undefined
  };
}

/******** Step 3: Payment ******/
function handleSaveServiceFeePaymentInfoStart(
  state: ServiceWorkOrderState,
  action: SaveServiceFeePaymentInfoStartAction
) {
  return {
    ...state,
    message: action.message
  };
}
function handleSaveServiceFeePaymentInfoSuccess(
  state: ServiceWorkOrderState,
  action: SaveServiceFeePaymentInfoSuccessAction
): ServiceWorkOrderState {
  return {
    ...state,
    message: action.message,
    id: action.serviceWorkOrderId,
    firstBilling: action.firstBilling,
    laterBills: action.laterBills
  };
}
function handleSaveServiceFeePaymentInfoFailure(
  state: ServiceWorkOrderState,
  action: SaveServiceFeePaymentInfoFailureAction
) {
  return {
    ...state,
    message: action.errorMessage
  };
}

/****** Step 4: Get/Add/Remove New Inventory/Exclusions *******/
function handleGetInventoryListStart(
  state: ServiceWorkOrderState,
  action: GetInventoryListStartAction
): ServiceWorkOrderState {
  return {
    ...state,
    inventoryLoading: true
  };
}
function handleGetInventoryListSuccess(
  state: ServiceWorkOrderState,
  action: GetInventoryListSuccessAction
): ServiceWorkOrderState {
  return {
    ...state,
    inventoryList: action.inventoryList.reverse(), // reverse array so most recent are first
    inventoryLoading: false
  };
}
function handleGetInventoryListFailure(
  state: ServiceWorkOrderState,
  action: GetInventoryListFailureAction
): ServiceWorkOrderState {
  return {
    ...state,
    message: action.message
  };
}

function handleAddNewInventory(state: ServiceWorkOrderState, action: AddNewInventoryAction): ServiceWorkOrderState {
  return {
    ...state
  };
}

function handleAddNewExclusionStart(
  state: ServiceWorkOrderState,
  action: AddNewExclusionStartAction
): ServiceWorkOrderState {
  return {
    ...state
  };
}
function handleAddNewExclusionSuccess(
  state: ServiceWorkOrderState,
  action: AddNewExclusionSuccessAction
): ServiceWorkOrderState {
  return {
    ...state
  };
}
function handleAddNewExclusionFailure(
  state: ServiceWorkOrderState,
  action: AddNewExclusionFailureAction
): ServiceWorkOrderState {
  return {
    ...state,
    message: action.message
  };
}

/****** Get Exclusion List ******/
function handleGetExclusionListStart(
  state: ServiceWorkOrderState,
  action: GetExclusionListStartAction
): ServiceWorkOrderState {
  return {
    ...state,
    exclusionsLoading: true
  };
}
function handleGetExclusionListSuccess(
  state: ServiceWorkOrderState,
  action: GetExclusionListSuccessAction
): ServiceWorkOrderState {
  return {
    ...state,
    exclusionList: action.exclusionList.reverse(), // reverse array so most recent are first
    exclusionsLoading: false
  };
}
function handleGetExclusionListFailure(
  state: ServiceWorkOrderState,
  action: GetExclusionListFailureAction
): ServiceWorkOrderState {
  return {
    ...state,
    message: action.message
  };
}

/****** Get Exclusion Types  *******/

function handleGetExclusionTypesStart(
  state: ServiceWorkOrderState,
  action: GetExclusionTypesStartAction
): ServiceWorkOrderState {
  return {
    ...state
  };
}

function handleGetExclusionTypesSuccess(
  state: ServiceWorkOrderState,
  action: GetExclusionTypesSuccessAction
): ServiceWorkOrderState {
  return {
    ...state,
    exclusionTypes: action.exclusionTypes
  };
}
function handleGetExclusionTypesFailure(
  state: ServiceWorkOrderState,
  action: GetExclusionTypesFailureAction
): ServiceWorkOrderState {
  return {
    ...state,
    message: action.message
  };
}

/********** SelectCVI **********/
function handleSelectCVI(state: ServiceWorkOrderState, action: SelectCVIAction): ServiceWorkOrderState {
  let selectedCVIs =
    state.selectedCVIs.indexOf(action.cvi.id) > -1
      ? state.selectedCVIs.filter(cviId => cviId !== action.cvi.id)
      : [...state.selectedCVIs, action.cvi.id];
  // Undefined and null is being placed into selected CVIs in another action/reducer
  selectedCVIs = selectedCVIs.filter(cvi => cvi !== null).filter(cvi => cvi !== undefined);
  return {
    ...state,
    selectedCVIs: selectedCVIs,
    serviceWorkOrderVM: { ...state.serviceWorkOrderVM!, contractCviIds: selectedCVIs }
  };
}

function addSelectedCVI(state: ServiceWorkOrderState, action: AddSelectedCVIAction): ServiceWorkOrderState {
  let selectedCVIs = [...state.selectedCVIs];
  selectedCVIs.push(action.cvi);
  // let swoVM = { ...state.serviceWorkOrderVM } as ServiceWorkOrderViewModel;
  // swoVM.contractCviId = ((selectedCVIs.length > 0) && swoVM) ? selectedCVIs[0] : undefined;
  return {
    ...state,
    selectedCVIs: selectedCVIs
    // serviceWorkOrderVM: swoVM,
  };
}

/********** GetServiceWorkOrderContractCVI **********/
function handleGetServiceWorkOrderContractCVIStart(
  state: ServiceWorkOrderState,
  action: GetServiceWorkOrderContractCVIStartAction
): ServiceWorkOrderState {
  return {
    ...state
  };
}
function handleGetServiceWorkOrderContractCVISuccess(
  state: ServiceWorkOrderState,
  action: GetServiceWorkOrderContractCVISuccessAction
): ServiceWorkOrderState {
  if (!action.swoContractCVIs) {
    return state;
  }
  return {
    ...state,
    inventoryList: action.swoContractCVIs
  };
}
function handleGetServiceWorkOrderContractCVIFailure(
  state: ServiceWorkOrderState,
  action: GetServiceWorkOrderContractCVIFailureAction
): ServiceWorkOrderState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    message: action.errorMessage
  };
}

/********** SaveServiceWorkOrderCreate **********/
function handleSaveServiceWorkOrderCreate(
  state: ServiceWorkOrderState,
  action: SaveServiceWorkOrderCreateAction
): ServiceWorkOrderState {
  let swo = { ...state.serviceWorkOrderVM, ...action.swo };
  return {
    ...state,
    serviceWorkOrderVM: swo
  };
}

function handleSaveNotes(state: ServiceWorkOrderState, action: SaveNotesAction): ServiceWorkOrderState {
  let selectedCVIs = [...state.selectedCVIs];
  return {
    ...state,
    serviceWorkOrderVM: {
      ...state.serviceWorkOrderVM,
      contractCviId: selectedCVIs.length > 0 ? selectedCVIs[0] : undefined,
      commentToVendor: action.vendorNotes,
      internalNotes: action.internalNotes
    } as ServiceWorkOrderViewModel
  };
}

/***** GetServiceWorkOrderContractCVI ******/
function handleGetContractCostHistoryStart(
  state: ServiceWorkOrderState,
  action: GetContractCostHistoryStartAction
): ServiceWorkOrderState {
  return {
    ...state,
    costHistoryLoading: true
  };
}
function handleGetContractCostHistorySuccess(
  state: ServiceWorkOrderState,
  action: GetContractCostHistorySuccessAction
): ServiceWorkOrderState {
  return {
    ...state,
    costHistory: action.costHistory,
    costHistoryLoading: false
  };
}
function handleGetContractCostHistoryFailure(
  state: ServiceWorkOrderState,
  action: GetContractCostHistoryFailureAction
): ServiceWorkOrderState {
  return {
    ...state,
    message: action.message
  };
}

function handleSetEditPM(state: ServiceWorkOrderState, action: SetEditPMAction): ServiceWorkOrderState {
  return {
    ...state,
    editPM: action.editPM
  };
}

/**** Edit Exclusion ****/
function handleEditExclusionStart(
  state: ServiceWorkOrderState,
  action: EditExclusionStartAction
): ServiceWorkOrderState {
  return {
    ...state
  };
}
function handleEditExclusionSuccess(
  state: ServiceWorkOrderState,
  action: EditExclusionSuccessAction
): ServiceWorkOrderState {
  return {
    ...state
  };
}
function handleEditExclusionFailure(
  state: ServiceWorkOrderState,
  action: EditExclusionFailureAction
): ServiceWorkOrderState {
  return {
    ...state,
    message: action.message
  };
}

/*** handleLoadSWO ***/

export function handleLoadSWO(state: ServiceWorkOrderState, action: LoadSWOAction): ServiceWorkOrderState {
  return {
    ...state,
    serviceWorkOrderVM: action.swo
  };
}

function handleAssignVendorStart(state: ServiceWorkOrderState, action: AssignVendorStartAction): ServiceWorkOrderState {
  return {
    ...state
  };
}
function handleAssignVendorSuccess(
  state: ServiceWorkOrderState,
  action: AssignVendorSuccessAction
): ServiceWorkOrderState {
  return {
    ...state,
    showSaveAndClose: true
  };
}
function handleAssignVendorFailure(
  state: ServiceWorkOrderState,
  action: AssignVendorFailureAction
): ServiceWorkOrderState {
  return {
    ...state,
    message: action.message
  };
}

function handleStoreVendor(state: ServiceWorkOrderState, action: StoreVendorAction): ServiceWorkOrderState {
  return {
    ...state,
    serviceWorkOrderVM: {
      ...state.serviceWorkOrderVM,
      vendorId: action.vendorId
    } as ServiceWorkOrderViewModel
  };
}

function handleResetSaveAndClose(state: ServiceWorkOrderState, action: ResetSaveAndCloseAction): ServiceWorkOrderState {
  return {
    ...state,
    showSaveAndClose: false
  };
}

/***** Redispatch SWO *****/
function handleRedispatchSWOFailure(
  state: ServiceWorkOrderState,
  action: RedispatchSWOFailureAction
): ServiceWorkOrderState {
  return state;
}
function handleRedispatchSWOStart(
  state: ServiceWorkOrderState,
  action: RedispatchSWOStartAction
): ServiceWorkOrderState {
  return state;
}
function handleRedispatchSWOSuccess(
  state: ServiceWorkOrderState,
  action: RedispatchSWOSuccessAction
): ServiceWorkOrderState {
  return state;
}

function handleGetSwoAppointmentsStart(
  state: ServiceWorkOrderState,
  action: GetSwoAppointmentsStartAction
): ServiceWorkOrderState {
  return state;
}

function handleGetSwoAppointmentsSuccess(
  state: ServiceWorkOrderState,
  action: GetSwoAppointmentsSuccessAction
): ServiceWorkOrderState {
  return {
    ...state,
    swoAppointments: action.swoAppointments
  };
}

function handleGetSwoAppointmentsFailure(
  state: ServiceWorkOrderState,
  action: GetSwoAppointmentsFailureAction
): ServiceWorkOrderState {
  return {
    ...state,
    message: action.errorMessage
  };
}

function handleGetSwoInvoicesStart(
  state: ServiceWorkOrderState,
  action: GetSwoInvoicesStartAction
): ServiceWorkOrderState {
  return state;
}

function handleGetSwoInvoicesSuccess(
  state: ServiceWorkOrderState,
  action: GetSwoInvoicesSuccessAction
): ServiceWorkOrderState {
  return {
    ...state,
    swoInvoices: action.swoInvoices
  };
}

function handleGetSwoInvoicesFailure(
  state: ServiceWorkOrderState,
  action: GetSwoInvoicesFailureAction
): ServiceWorkOrderState {
  return {
    ...state,
    message: action.errorMessage
  };
}
/**** GetTripStatusLog *****/

function handleGetTripStatusLogStart(
  state: ServiceWorkOrderState,
  action: GetTripStatusLogStartAction
): ServiceWorkOrderState {
  return {
    ...state,
    loading: true
  };
}
function handleGetTripStatusLogSuccess(
  state: ServiceWorkOrderState,
  action: GetTripStatusLogSuccessAction
): ServiceWorkOrderState {
  return {
    ...state,
    loading: false,
    tripStatusLog: action.statusLog
  };
}
function handleGetTripStatusLogFailure(
  state: ServiceWorkOrderState,
  action: GetTripStatusLogFailureAction
): ServiceWorkOrderState {
  return {
    ...state,
    loading: false,
    message: action.message
  };
}

/**** Clear Swo Details ****/

function handleClearSwoDetails(state: ServiceWorkOrderState, action: ClearSwoDetailsAction): ServiceWorkOrderState {
  return {
    ...initialServiceWorkOrderState
  };
}

function handleSetTypes(state: ServiceWorkOrderState, action: SetTypesAction): ServiceWorkOrderState {
  return {
    ...state,
    types: action.swoTypes
  };
}

function handleSetTypeId(state: ServiceWorkOrderState, action: SetTypeIdAction): ServiceWorkOrderState {
  if (!state.serviceWorkOrderVM) {
    return state;
  }
  return {
    ...state,
    serviceWorkOrderVM: {
      ...state.serviceWorkOrderVM,
      type: { id: action.typeId } as OptionsViewModel
    }
  };
}

function handleSetSwoTicketId(state: ServiceWorkOrderState, action: SetSwoTicketIdAction): ServiceWorkOrderState {
  return {
    ...state,
    linkTicketId: action.id
  };
}

function handleSetSecondOpinionSWO(
  state: ServiceWorkOrderState,
  action: SetSecondOpinionSWOAction
): ServiceWorkOrderState {
  if (!state.serviceWorkOrderVM) {
    return state;
  }
  let secondOpinionSWO = {
    ...state.serviceWorkOrderVM,
    id: 0,
    type: { id: action.swoTypeId } as OptionsViewModel,
    status: { id: -1 } as OptionsViewModel,
    vendorId: 0,
    parentSwo: action.parentSWOId,
    parentVendorId: action.parentVendorId,
    childSWOIds: []
  };
  if (action.comment && action.comment.trim().length > 0) {
    secondOpinionSWO.internalNotes += ' ' + action.comment;
  }
  return {
    ...state,
    serviceWorkOrderVM: secondOpinionSWO
  };
}

export const ServiceWorkOrderReducer = createReducer<ServiceWorkOrderState, ServiceWorkOrderActionType>(
  initialServiceWorkOrderState,
  {
    /****** Get SWO ******/
    [ServiceWorkOrderActionType.GetSWOStart]: handleGetSwoStart,
    [ServiceWorkOrderActionType.GetSWOSuccess]: handleGetSwoSuccess,
    [ServiceWorkOrderActionType.GetSWOFailure]: handleGetSwoFailure,

    /********** SaveServiceWorkOrder **********/
    [ServiceWorkOrderActionType.SaveServiceWorkOrderStart]: handleSaveServiceWorkOrderStart,
    [ServiceWorkOrderActionType.SaveServiceWorkOrderSuccess]: handleSaveServiceWorkOrderSuccess,
    [ServiceWorkOrderActionType.SaveServiceWorkOrderFailure]: handleSaveServiceWorkOrderFailure,

    /***** Search SWO ******/
    [ServiceWorkOrderActionType.SearchServiceWorkOrderStart]: handleSearchServiceWorkOrderStart,
    [ServiceWorkOrderActionType.SearchServiceWorkOrderSuccess]: handleSearchServiceWorkOrderSuccess,
    [ServiceWorkOrderActionType.SearchServiceWorkOrderFailure]: handleSearchServiceWorkOrderFailure,
    [ServiceWorkOrderActionType.ResetSWOSearch]: handleResetSWOSearch,

    /***** GetServiceWorkOrderContract ******/
    [ServiceWorkOrderActionType.GetServiceWorkOrderContractStart]: handleGetServiceWorkOrderContractStart,
    [ServiceWorkOrderActionType.GetServiceWorkOrderContractSuccess]: handleGetServiceWorkOrderContractSuccess,
    [ServiceWorkOrderActionType.GetServiceWorkOrderContractFailure]: handleGetServiceWorkOrderContractFailure,
    [ServiceWorkOrderActionType.LoadPropertyManager]: handleLoadPropertyManager,
    [ServiceWorkOrderActionType.ClearPropertyManager]: handleClearPropertyManager,

    /******* Step 3: Service Fee Payment *******/
    [ServiceWorkOrderActionType.SaveServiceFeePaymentInfoStart]: handleSaveServiceFeePaymentInfoStart,
    [ServiceWorkOrderActionType.SaveServiceFeePaymentInfoSuccess]: handleSaveServiceFeePaymentInfoSuccess,
    [ServiceWorkOrderActionType.SaveServiceFeePaymentInfoFailure]: handleSaveServiceFeePaymentInfoFailure,

    /**** Step 4: Add/Remove New Inventory ****/
    [ServiceWorkOrderActionType.GetInventoryListStart]: handleGetInventoryListStart,
    [ServiceWorkOrderActionType.GetInventoryListSuccess]: handleGetInventoryListSuccess,
    [ServiceWorkOrderActionType.GetInventoryListFailure]: handleGetInventoryListFailure,

    [ServiceWorkOrderActionType.AddNewInventory]: handleAddNewInventory,

    /*** Get Exclusion List****/
    [ServiceWorkOrderActionType.GetExclusionListStart]: handleGetExclusionListStart,
    [ServiceWorkOrderActionType.GetExclusionListSuccess]: handleGetExclusionListSuccess,
    [ServiceWorkOrderActionType.GetExclusionListFailure]: handleGetExclusionListFailure,

    /**** Get Exclusion Types ****/
    [ServiceWorkOrderActionType.GetExclusionTypesStart]: handleGetExclusionTypesStart,
    [ServiceWorkOrderActionType.GetExclusionTypesSuccess]: handleGetExclusionTypesSuccess,
    [ServiceWorkOrderActionType.GetExclusionTypesFailure]: handleGetExclusionTypesFailure,

    /********** SelectCVI **********/
    [ServiceWorkOrderActionType.SelectCVI]: handleSelectCVI,
    [ServiceWorkOrderActionType.AddSelectedCVI]: addSelectedCVI,

    /***** GetServiceWorkOrderContractCVI ******/
    [ServiceWorkOrderActionType.GetServiceWorkOrderContractCVIStart]: handleGetServiceWorkOrderContractCVIStart,
    [ServiceWorkOrderActionType.GetServiceWorkOrderContractCVISuccess]: handleGetServiceWorkOrderContractCVISuccess,
    [ServiceWorkOrderActionType.GetServiceWorkOrderContractCVIFailure]: handleGetServiceWorkOrderContractCVIFailure,

    [ServiceWorkOrderActionType.AddNewExclusionStart]: handleAddNewExclusionStart,
    [ServiceWorkOrderActionType.AddNewExclusionSuccess]: handleAddNewExclusionSuccess,
    [ServiceWorkOrderActionType.AddNewExclusionFailure]: handleAddNewExclusionFailure,

    /********** SaveServiceWorkOrderCreate **********/
    [ServiceWorkOrderActionType.SaveServiceWorkOrderCreate]: handleSaveServiceWorkOrderCreate,
    [ServiceWorkOrderActionType.SaveNotes]: handleSaveNotes,

    /***** Get Contract Cost History******/
    [ServiceWorkOrderActionType.GetContractCostHistoryStart]: handleGetContractCostHistoryStart,
    [ServiceWorkOrderActionType.GetContractCostHistorySuccess]: handleGetContractCostHistorySuccess,
    [ServiceWorkOrderActionType.GetContractCostHistoryFailure]: handleGetContractCostHistoryFailure,

    /**** Edit Exclusion ****/
    [ServiceWorkOrderActionType.EditExclusionStart]: handleEditExclusionStart,
    [ServiceWorkOrderActionType.EditExclusionSuccess]: handleEditExclusionSuccess,
    [ServiceWorkOrderActionType.EditExclusionFailure]: handleEditExclusionFailure,

    [ServiceWorkOrderActionType.LoadSWO]: handleLoadSWO,

    [ServiceWorkOrderActionType.AssignVendorStart]: handleAssignVendorStart,
    [ServiceWorkOrderActionType.AssignVendorSuccess]: handleAssignVendorSuccess,
    [ServiceWorkOrderActionType.AssignVendorFailure]: handleAssignVendorFailure,
    [ServiceWorkOrderActionType.ResetSaveAndClose]: handleResetSaveAndClose,
    [ServiceWorkOrderActionType.StoreVendor]: handleStoreVendor,
    /***** Redispatch SWO *****/
    [ServiceWorkOrderActionType.RedispatchSWOFailure]: handleRedispatchSWOFailure,
    [ServiceWorkOrderActionType.RedispatchSWOStart]: handleRedispatchSWOStart,
    [ServiceWorkOrderActionType.RedispatchSWOSuccess]: handleRedispatchSWOSuccess,

    /**** Get Swo Appointments */
    [ServiceWorkOrderActionType.GetSwoAppointmentsStart]: handleGetSwoAppointmentsStart,
    [ServiceWorkOrderActionType.GetSwoAppointmentsSuccess]: handleGetSwoAppointmentsSuccess,
    [ServiceWorkOrderActionType.GetSwoAppointmentsFailure]: handleGetSwoAppointmentsFailure,

    /**** Get Swo Invoices */
    [ServiceWorkOrderActionType.GetSwoInvoicesStart]: handleGetSwoInvoicesStart,
    [ServiceWorkOrderActionType.GetSwoInvoicesSuccess]: handleGetSwoInvoicesSuccess,
    [ServiceWorkOrderActionType.GetSwoInvoicesFailure]: handleGetSwoInvoicesFailure,
    /**** GetTripStatusLog ****/
    [ServiceWorkOrderActionType.GetTripStatusLogStart]: handleGetTripStatusLogStart,
    [ServiceWorkOrderActionType.GetTripStatusLogSuccess]: handleGetTripStatusLogSuccess,
    [ServiceWorkOrderActionType.GetTripStatusLogFailure]: handleGetTripStatusLogFailure,

    /***** Clear Swo Details *****/
    [ServiceWorkOrderActionType.ClearSwoDetails]: handleClearSwoDetails,

    [ServiceWorkOrderActionType.ResetServiceWorkOrder]: state => initialServiceWorkOrderState,
    [ServiceWorkOrderActionType.SetEditPM]: handleSetEditPM,

    [ServiceWorkOrderActionType.SetTypes]: handleSetTypes,
    [ServiceWorkOrderActionType.SetTypeId]: handleSetTypeId,
    [ServiceWorkOrderActionType.SetSecondOpinionSWO]: handleSetSecondOpinionSWO,
    [ServiceWorkOrderActionType.SetSwoTicketId]: handleSetSwoTicketId
  }
);



// WEBPACK FOOTER //
// ./src/service-work-order/service-work-order.reducer.tsx